import { useEffect, useRef, type ReactNode, type Ref } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button } from '../controls/Button'
import s from './CustomizationModeBar.module.css'

export type CustomizationModeStatus = 'ready' | 'dirty' | 'saving' | 'saved' | 'error'

export interface CustomizationModeBarProps {
  /** The governed audience/layer being changed, for example "only you". */
  scope: ReactNode
  /** A complete, host-owned count sentence ("12 links showing"). */
  count: ReactNode
  /** True once the working document differs from the persisted document. */
  dirty: boolean
  /** Keeps the draft visible while its host-owned mutation is in flight. */
  busy?: boolean
  /** Optional host status; errors and later recovery can use the same live seam. */
  status?: ReactNode
  statusTone?: CustomizationModeStatus
  /** Optional actionable status, such as focusing rows that require repair. */
  statusAction?: { label: string; onClick: () => void }
  /** Optional explanatory or governed-host guidance. */
  note?: ReactNode
  /** Whether Cancel/Escape has moved the bar into its inline discard state. */
  confirmingDiscard?: boolean
  /** A host can keep its existing focus hand-off without the primitive owning it. */
  keepEditingRef?: Ref<HTMLButtonElement>
  onSave: () => void
  onCancel: () => void
  onKeepEditing?: () => void
  onDiscard?: () => void
  onReset?: () => void
  saveDisabled?: boolean
  noun?: string
}

/**
 * The shared, data-free action surface for an in-place customization mode.
 *
 * It owns presentation and status semantics only. `NavigationEditSurface`
 * owns navigation draft/exit mechanics; hosts keep their governed doors.
 */
export function CustomizationModeBar({
  scope,
  count,
  dirty,
  busy = false,
  status,
  statusTone,
  statusAction,
  note,
  confirmingDiscard = false,
  keepEditingRef,
  onSave,
  onCancel,
  onKeepEditing,
  onDiscard,
  onReset,
  saveDisabled = false,
  noun = 'navigation',
}: CustomizationModeBarProps) {
  const barRef = useRef<HTMLElement>(null)
  const resolvedTone: CustomizationModeStatus = statusTone
    ?? (busy ? 'saving' : dirty ? 'dirty' : 'ready')
  const resolvedStatus = status
    ?? (busy ? 'Saving changes…' : dirty ? 'Unsaved changes' : 'Ready to save')

  /* The content can gain a host note or a translated status, so a guessed
     height is only a first-paint fallback. Keep the shared shell reserve equal
     to the rendered bar plus breathing room; both hosts consume this one
     variable and cleanup restores the value that was there before the mode. */
  useEffect(() => {
    const bar = barRef.current
    if (!bar || typeof ResizeObserver === 'undefined') return
    const root = document.documentElement
    const previous = root.style.getPropertyValue('--customization-mode-bar-reserve')
    const measure = () => {
      const height = Math.ceil(bar.getBoundingClientRect().height)
      const next = `${height + 24}px`
      if (height > 0 && root.style.getPropertyValue('--customization-mode-bar-reserve') !== next) {
        root.style.setProperty('--customization-mode-bar-reserve', next)
      }
    }
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    const observer = new ResizeObserver(schedule)
    observer.observe(bar)
    measure()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      if (previous) root.style.setProperty('--customization-mode-bar-reserve', previous)
      else root.style.removeProperty('--customization-mode-bar-reserve')
    }
  }, [])

  return (
    <section
      ref={barRef}
      className={s.bar}
      aria-label={`${noun} customization controls`}
      aria-busy={busy || undefined}
      data-testid="nav-edit-dock"
    >
      {confirmingDiscard ? (
        <>
          <div className={s.summary}>
            <span className={s.prompt} data-testid="nav-edit-discard">
              Discard your unsaved {noun} changes?
            </span>
            <span className={s.status} role="status" aria-live="polite">
              Your working changes are still here.
            </span>
          </div>
          <div className={s.actions}>
            <Button
              size="sm"
              variant="ghost"
              ref={keepEditingRef}
              data-testid="nav-edit-keep"
              onClick={onKeepEditing}
            >
              Keep editing
            </Button>
            <Button
              size="sm"
              variant="danger"
              data-testid="nav-edit-discard-confirm"
              onClick={onDiscard}
            >
              Discard
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className={s.summary}>
            <span className={s.scope} data-testid="nav-edit-layer">
              Editing {noun} · <strong>{scope}</strong>
            </span>
            <span className={s.count} data-testid="nav-edit-count">{count}</span>
            <span className={s.status} data-tone={resolvedTone} role="status" aria-live="polite">
              {resolvedStatus}
            </span>
            {statusAction && (
              <Button size="sm" variant="ghost" className={s.statusAction}
                onClick={statusAction.onClick}>{statusAction.label}</Button>
            )}
            {note && <span className={s.note}>{note}</span>}
          </div>
          <div className={s.actions}>
            {onReset && (
              <Button
                size="sm"
                variant="ghost"
                icon={<RotateCcw />}
                disabled={busy}
                data-testid="nav-edit-reset"
                onClick={onReset}
              >
                Reset
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              data-testid="nav-edit-cancel"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              loading={busy}
              disabled={saveDisabled}
              data-testid="nav-edit-save"
              onClick={onSave}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
